import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import paymentVelocityServices from 'services/paymentVelocity.services';

// ERR_NO_VELOCITY_RESTRICTION comes back when a recover is attempted on a driver
// no fraud rule blocked. The inactive-driver cron also leaves a driver locked, so
// the API refuses until the operator confirms with force.
export const ERR_NO_VELOCITY_RESTRICTION = 'ERR_NO_VELOCITY_RESTRICTION';

export default function useFetchPaymentVelocity() {
  const [restrictions, setRestrictions] = useState(null);
  const [incidents, setIncidents] = useState({});
  const [releaseLogs, setReleaseLogs] = useState({});
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  // forcePrompt holds the message the API refused with, which the confirm dialog
  // shows before resending with force.
  const [forcePrompt, setForcePrompt] = useState(null);

  const fetchRestrictions = useCallback(async userId => {
    if (!userId) return;

    try {
      setLoading(true);
      const data = await paymentVelocityServices.userRestrictions(userId);
      setRestrictions(data);
    } catch (err) {
      // A 403 means this operator cannot see fraud data. The caller hides the
      // whole block, so there is nothing to report.
      setRestrictions(null);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIncidents = useCallback(async userId => {
    if (!userId) return;

    try {
      const data = await paymentVelocityServices.incidents({ userId });
      setIncidents(data || {});
    } catch (err) {
      setIncidents({});
      console.error(err);
    }
  }, []);

  const fetchReleaseLogs = useCallback(async userId => {
    if (!userId) return;

    try {
      const data = await paymentVelocityServices.releaseLogs(userId);
      setReleaseLogs(data || {});
    } catch (err) {
      setReleaseLogs({});
      console.error(err);
    }
  }, []);

  const refresh = useCallback(
    async userId => {
      await Promise.all([
        fetchRestrictions(userId),
        fetchIncidents(userId),
        fetchReleaseLogs(userId)
      ]);
    },
    [fetchRestrictions, fetchIncidents, fetchReleaseLogs]
  );

  // reportStripeErrors is why every action funnels through one place: a partial
  // success has to warn rather than read as done. The block was lifted or
  // applied, but a Stripe payout schedule was not, and a driver whose payouts
  // stay suspended is a silent failure.
  //
  // This toast is the only notice, so it has to stand on its own. The error text
  // is deliberately not in it: those are Stripe API strings an operator cannot
  // act on, and they are kept in the server logs and on the audit row.
  const reportStripeErrors = (result, successMessage) => {
    if (result?.has_stripe_errors) {
      toast.warning(
        'Done, but the Stripe payout schedule was not updated. It needs fixing by hand in Stripe.'
      );

      return;
    }

    toast.success(successMessage);
  };

  const runAction = useCallback(
    async ({ userId, action, successMessage, onDone }) => {
      try {
        setActing(true);
        setForcePrompt(null);

        const result = await action();
        reportStripeErrors(result, successMessage);
        await refresh(userId);

        if (onDone) onDone(result);

        return result;
      } catch (err) {
        if (err?.error?.type === ERR_NO_VELOCITY_RESTRICTION) {
          // Not a failure to report as one: the operator is being asked to
          // confirm an unlock the API would not do on its own.
          setForcePrompt(err.error.message);

          return null;
        }

        toast.error(err?.error?.message || 'The action has failed.');

        return null;
      } finally {
        setActing(false);
      }
    },
    [refresh]
  );

  // An empty types array recovers every block. Selecting types narrows it, so an
  // operator can hand sign-in back while a payouts hold stays on.
  const release = useCallback(
    ({ userId, types = [], force = false, note = '', onDone }) =>
      runAction({
        userId,
        action: () => paymentVelocityServices.release(userId, { types, force, note }),
        successMessage: force
          ? 'The driver was unlocked.'
          : types.length > 0
          ? 'The selected blocks were released.'
          : 'All blocks were released.',
        onDone
      }),
    [runAction]
  );

  const lock = useCallback(
    ({ userId, types, note = '', onDone }) =>
      runAction({
        userId,
        action: () => paymentVelocityServices.lock(userId, { types, note }),
        successMessage: 'The restrictions were applied.',
        onDone
      }),
    [runAction]
  );

  const dismissForcePrompt = useCallback(() => setForcePrompt(null), []);

  return {
    restrictions,
    incidents,
    releaseLogs,
    loading,
    acting,
    forcePrompt,
    fetchRestrictions,
    fetchIncidents,
    fetchReleaseLogs,
    refresh,
    release,
    lock,
    dismissForcePrompt
  };
}
